import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals';

interface PerformanceMetrics {
  cls?: number;
  inp?: number;
  fcp?: number;
  lcp?: number;
  ttfb?: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics = {};
  private listeners: ((metrics: PerformanceMetrics) => void)[] = [];

  constructor() {
    this.initializeMetrics();
  }

  private initializeMetrics() {
    // Core Web Vitals
    onCLS((metric) => {
      this.metrics.cls = metric.value;
      this.notifyListeners();
      console.log('CLS (Cumulative Layout Shift):', metric.value);
    });

    onINP((metric) => {
      this.metrics.inp = metric.value;
      this.notifyListeners();
      console.log('INP (Interaction to Next Paint):', metric.value, 'ms');
    });

    onLCP((metric) => {
      this.metrics.lcp = metric.value;
      this.notifyListeners();
      console.log('LCP (Largest Contentful Paint):', metric.value, 'ms');
    });

    // Additional metrics
    onFCP((metric) => {
      this.metrics.fcp = metric.value;
      this.notifyListeners();
      console.log('FCP (First Contentful Paint):', metric.value, 'ms');
    });

    onTTFB((metric) => {
      this.metrics.ttfb = metric.value;
      this.notifyListeners();
      console.log('TTFB (Time to First Byte):', metric.value, 'ms');
    });
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.metrics));
  }

  public subscribe(listener: (metrics: PerformanceMetrics) => void) {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  // Get current metrics immediately (useful for reporting)
  public getCurrentMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Utility function to get performance grade
export function getPerformanceGrade(metrics: PerformanceMetrics): {
  cls: 'good' | 'needs-improvement' | 'poor' | 'unknown';
  inp: 'good' | 'needs-improvement' | 'poor' | 'unknown';
  lcp: 'good' | 'needs-improvement' | 'poor' | 'unknown';
  overall: 'good' | 'needs-improvement' | 'poor';
} {
  const gradeMetric = (value: number | undefined, goodThreshold: number, poorThreshold: number) => {
    if (value === undefined) return 'unknown';
    if (value <= goodThreshold) return 'good';
    if (value <= poorThreshold) return 'needs-improvement';
    return 'poor';
  };

  const grades = {
    cls: gradeMetric(metrics.cls, 0.1, 0.25) as 'good' | 'needs-improvement' | 'poor' | 'unknown',
    inp: gradeMetric(metrics.inp, 200, 500) as 'good' | 'needs-improvement' | 'poor' | 'unknown',
    lcp: gradeMetric(metrics.lcp, 2500, 4000) as 'good' | 'needs-improvement' | 'poor' | 'unknown',
    overall: 'good' as 'good' | 'needs-improvement' | 'poor'
  };

  // Overall grade based on worst performing metric
  const scores = [grades.cls, grades.inp, grades.lcp].filter(grade => grade !== 'unknown');
  if (scores.includes('poor')) grades.overall = 'poor';
  else if (scores.includes('needs-improvement')) grades.overall = 'needs-improvement';
  else grades.overall = 'good';

  return grades;
}

// Utility function to measure calculation performance
export function measureCalculationPerformance<T>(
  calculationName: string,
  calculationFn: () => T
): T {
  const startTime = performance.now();
  const result = calculationFn();
  const endTime = performance.now();
  const duration = endTime - startTime;

  console.log(`${calculationName} performance:`, duration.toFixed(2), 'ms');

  // Log warning if calculation is slow
  if (duration > 100) {
    console.warn(`Slow calculation detected: ${calculationName} took ${duration.toFixed(2)}ms`);
  }

  return result;
}

export type { PerformanceMetrics };